import { Injectable } from '@angular/core';
import {
    HttpBackend, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Sample inspection (setup guide "View sample inspection"): /inspections-completed-sheet/sample and
 * every other inspection page with the job id "sample" show made-up data, with no changes to the
 * pages themselves.
 *
 * While a sample page is open, the app's API requests are answered from
 * assets/sample-inspection/fixtures.json (recorded from a real job's pages, then anonymized: ids
 * that match no record, made-up people and addresses). Company-wide reads below stay live. Anything
 * else - every save, delete, generate or send - is refused with a message, so nothing is written.
 * Real jobs never reach this code path.
 */
export class SampleInspection {
    static readonly JOB_ID = 'sample';
    /** Window event fired when a change is refused (the sample banner says so; some pages stay silent). */
    static readonly BLOCKED_EVENT = 'im-sample-blocked';
    /** True while the router is on (or navigating to) a sample inspection page. */
    static active = SampleInspection.isSampleUrl(window.location.pathname);

    static isSampleUrl(url: string): boolean {
        return /\/sample(?:[\/?#]|$)/.test((url || '').split('?')[0] + '/');
    }
}

// Reads that are not about the job and are safe to keep live on a sample page.
const LIVE_READS = ['account', 'get_setting_data', 'user-access', 'user-preferences', 'lockout/settings', 'wdo/config',
    'inspections-list/overview-access', 'notifications'];
const LIVE_WRITES = ['user-preferences'];   // layout and collapsed-panel preferences

@Injectable()
export class SampleInspectionInterceptor implements HttpInterceptor {
    private fixtures$: Observable<{ [key: string]: any }>;

    // HttpBackend: the fixtures file itself is fetched without going through the interceptors.
    constructor(private backend: HttpBackend) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const apiPath = SampleInspectionInterceptor.apiPath(request.urlWithParams);
        if (apiPath === null || !(SampleInspection.active || SampleInspection.isSampleUrl(apiPath))) {
            return next.handle(request);
        }
        const path = apiPath.split('?')[0];
        if ((request.method === 'GET' && LIVE_READS.indexOf(path) !== -1) || LIVE_WRITES.indexOf(path) !== -1) {
            return next.handle(request);
        }
        const key = request.method + ' ' + apiPath;
        return this.fixtures().pipe(mergeMap(fixtures => {
            if (fixtures.hasOwnProperty(key)) {
                return of(new HttpResponse({ status: 200, body: fixtures[key], url: request.url }));
            }
            if (request.method !== 'GET') {
                window.dispatchEvent(new CustomEvent(SampleInspection.BLOCKED_EVENT));
            }
            return throwError(new HttpErrorResponse({
                status: 403,
                url: request.url,
                error: {
                    message: request.method === 'GET'
                        ? 'This is not available in the sample inspection.'
                        : 'This is a sample inspection. Changes are not saved.'
                }
            }));
        }));
    }

    private fixtures(): Observable<{ [key: string]: any }> {
        if (!this.fixtures$) {
            this.fixtures$ = this.backend.handle(new HttpRequest('GET', 'assets/sample-inspection/fixtures.json')).pipe(
                map(event => event instanceof HttpResponse ? event.body : null),
                mergeMap(body => body ? of(body) : []),
                shareReplay(1)
            );
        }
        return this.fixtures$;
    }

    /** The part after the app's API base ("get_job/sample?x=1"), or null for any other URL. */
    private static apiPath(url: string): string {
        return url.indexOf(environment.api) === 0 ? url.slice(environment.api.length) : null;
    }
}
